import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PartnershipCard from './partnerships/PartnershipCard';
import PartnershipStatsBar from './partnerships/PartnershipStatsBar';
import NewPartnershipModal from './partnerships/NewPartnershipModal';
import EmptyState from '../common/EmptyState';
import { usePartnerships } from '../../hooks/usePartnerships';

const { FiPlus, FiRefreshCw, FiHandshake } = FiIcons;

const STATUS_FILTERS = ['all', 'active', 'pending', 'ended'];

const FEATURE_CARDS = [
  { title: 'Revenue Share',  desc: 'Split earnings from releases or content automatically.' },
  { title: 'Co-Releases',   desc: 'Launch music together and cross-promote to both fanbases.' },
  { title: 'Tour & Merch',  desc: 'Coordinate live events and merch lines with partners.' },
];

const PartnershipSection = ({ artistId, userId, isOwner, artistName, onToast }) => {
  const {
    partnerships, loading, error, refetch,
    createPartnership, updateStatus, updateTerms, agreeToTerms,
    addMilestone, updateMilestone, deleteMilestone, toggleMilestone,
  } = usePartnerships(artistId, { onToast });

  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);

  const visible = statusFilter === 'all'
    ? partnerships
    : partnerships.filter(p => p.status === statusFilter);

  const handleCreate = (payload) =>
    createPartnership({ ...payload, _initiatorName: artistName });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-zinc-900 pb-8">
        <div className="space-y-2">
          <span className="text-xs font-black tracking-widest text-indigo-400 uppercase">Deals</span>
          <h2 className="text-5xl font-black tracking-tighter text-white uppercase leading-none">Partnerships</h2>
          <p className="text-zinc-400 font-bold text-base">Formal artist agreements &amp; collaboration deals</p>
        </div>
        <div className="flex items-center gap-3 self-start">
          <button onClick={refetch}
            className="flex items-center gap-2 px-5 py-3 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-600 hover:text-white transition-all">
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
          </button>
          {isOwner && (
            <button onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-black uppercase tracking-widest rounded-2xl hover:scale-105 active:scale-95 transition-all">
              <SafeIcon icon={FiPlus} className="text-sm" /> New Partnership
            </button>
          )}
        </div>
      </div>

      {partnerships.length > 0 && <PartnershipStatsBar partnerships={partnerships} />}

      {partnerships.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURE_CARDS.map(({ title, desc }) => (
            <div key={title} className="p-6 bg-zinc-900/30 border border-zinc-800/50 rounded-2xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                <SafeIcon icon={FiHandshake} className="text-indigo-400 text-lg" />
              </div>
              <p className="font-black text-white uppercase tracking-tight">{title}</p>
              <p className="text-zinc-500 text-sm font-bold leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      )}

      {partnerships.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map(f => {
            const count = f === 'all' ? partnerships.length : partnerships.filter(p => p.status === f).length;
            return (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                  statusFilter === f ? 'bg-white text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}>
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${statusFilter === f ? 'bg-black/20' : 'bg-zinc-800'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {visible.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visible.map(p => (
            <PartnershipCard
              key={p.id}
              partnership={p}
              isOwner={isOwner}
              userId={userId}
              onUpdateStatus={updateStatus}
              onToggleMilestone={toggleMilestone}
              onUpdateTerms={updateTerms}
              onAgreeToTerms={agreeToTerms}
              onAddMilestone={addMilestone}
              onUpdateMilestone={updateMilestone}
              onDeleteMilestone={deleteMilestone}
            />
          ))}
        </div>
      ) : partnerships.length > 0 ? (
        <EmptyState title={`No ${statusFilter} partnerships`} subtitle="Try a different filter." />
      ) : (
        isOwner && (
          <div className="flex justify-center pt-4">
            <button onClick={() => setShowModal(true)}
              className="px-8 py-4 bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-black uppercase tracking-widest text-sm rounded-2xl hover:bg-indigo-500/20 transition-all">
              + Create Your First Partnership
            </button>
          </div>
        )
      )}

      {showModal && (
        <NewPartnershipModal
          artistId={artistId}
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

export default PartnershipSection;